import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Bell, AlertTriangle, CheckCircle, RefreshCw, Send, X, Users, User, Search, Briefcase, Clock, PauseCircle, Timer } from 'lucide-react';
import api from '../api';
import { useAuth } from '../contexts/AuthContext';

// All authenticated users can send notifications

// ──────────────────────────────────────────────────────────────
// Send Notification Modal (for privileged users)
// ──────────────────────────────────────────────────────────────
const SendNotificationModal = ({ onClose, onSent, token }) => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [recipientMode, setRecipientMode] = useState('all'); // 'all' | 'individual' | 'bulk'
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [userFilter, setUserFilter] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get('/admin/users', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = Array.isArray(res.data) ? res.data : (res.data?.users || []);
        setUsers(data);
      } catch (e) {
        setUsers([]);
      }
    };
    fetchUsers();
  }, [token]);

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredUsers = users.filter((u) =>
    userFilter === '' || (u.name || '').toLowerCase().includes(userFilter.toLowerCase())
  );

  const handleSend = async () => {
    if (!title.trim() || !message.trim()) {
      setError('Title and message are required.');
      return;
    }
    if (recipientMode !== 'all' && selectedIds.length === 0) {
      setError('Please select at least one recipient.');
      return;
    }
    setSending(true);
    setError('');
    try {
      await api.post(
        '/notifications',
        {
          title: title.trim(),
          message: message.trim(),
          priority,
          recipient_ids: recipientMode === 'all' ? [] : selectedIds,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSent();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send notification.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Send className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-800">Send Notification</h3>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Notification title…"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Write your message…"
            />
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
            <div className="flex gap-3">
              {['normal', 'high'].map((p) => (
                <button
                  key={p}
                  onClick={() => setPriority(p)}
                  className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors capitalize ${
                    priority === p
                      ? p === 'high'
                        ? 'bg-red-600 text-white border-red-600'
                        : 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Recipients */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Recipients</label>
            <div className="flex gap-2 mb-3">
              {[
                { id: 'all', label: 'All Users', icon: <Users className="w-3.5 h-3.5" /> },
                { id: 'individual', label: 'Individual', icon: <User className="w-3.5 h-3.5" /> },
                { id: 'bulk', label: 'Bulk Select', icon: <Users className="w-3.5 h-3.5" /> },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => { setRecipientMode(m.id); setSelectedIds([]); }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    recipientMode === m.id
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {m.icon}
                  {m.label}
                </button>
              ))}
            </div>

            {recipientMode !== 'all' && (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-2 bg-gray-50 border-b border-gray-200">
                  <input
                    type="text"
                    value={userFilter}
                    onChange={(e) => setUserFilter(e.target.value)}
                    placeholder="Filter users…"
                    className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-400"
                  />
                </div>
                <div className="max-h-40 overflow-y-auto divide-y divide-gray-100">
                  {filteredUsers.length === 0 && (
                    <p className="px-3 py-3 text-sm text-gray-500">No users found.</p>
                  )}
                  {filteredUsers.map((u) => {
                    const uid = String(u.id || u._id);
                    const checked = selectedIds.includes(uid);
                    return (
                      <label
                        key={uid}
                        className={`flex items-center gap-2 px-3 py-2 cursor-pointer text-sm hover:bg-gray-50 ${
                          checked ? 'bg-blue-50' : ''
                        }`}
                      >
                        <input
                          type={recipientMode === 'individual' ? 'radio' : 'checkbox'}
                          name="recipient"
                          checked={checked}
                          onChange={() => {
                            if (recipientMode === 'individual') {
                              setSelectedIds([uid]);
                            } else {
                              toggleUser(uid);
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span className="font-medium text-gray-800">{u.name}</span>
                        <span className="text-gray-400 text-xs ml-auto">{u.role}</span>
                      </label>
                    );
                  })}
                </div>
                {selectedIds.length > 0 && (
                  <div className="px-3 py-2 bg-blue-50 border-t border-gray-200 text-xs text-blue-700 font-medium">
                    {selectedIds.length} recipient{selectedIds.length > 1 ? 's' : ''} selected
                  </div>
                )}
              </div>
            )}
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Send className="w-4 h-4" />
            {sending ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────
// Notification badge chips
// ──────────────────────────────────────────────────────────────
const TypeBadge = ({ type }) => {
  if (type === 'approval_rejected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
        <AlertTriangle className="w-3 h-3" /> Rejection
      </span>
    );
  }
  if (type === 'approval_approved') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">
        <CheckCircle className="w-3 h-3" /> Approved
      </span>
    );
  }
  if (type === 'no_jobs') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
        <Briefcase className="w-3 h-3" /> No Jobs
      </span>
    );
  }
  if (type === 'task_hold') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <Clock className="w-3 h-3" /> On Hold
      </span>
    );
  }
  if (type === 'task_pause') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
        <PauseCircle className="w-3 h-3" /> Paused
      </span>
    );
  }
  if (type === 'task_overtime') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <Timer className="w-3 h-3" /> Overtime
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
      <Bell className="w-3 h-3" /> Notification
    </span>
  );
};

// ──────────────────────────────────────────────────────────────
// Main Notifications Page
// ──────────────────────────────────────────────────────────────
const Notifications = () => {
  const { token, user } = useAuth();
  const canSend = !!user;
  const isAdmin = user?.role === 'admin';

  // Data
  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  // Filters
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');       // '' | 'manual' | 'approval_rejected'
  const [filterPriority, setFilterPriority] = useState(''); // '' | 'normal' | 'high'
  const [filterRead, setFilterRead] = useState('');         // '' | 'read' | 'unread'
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const searchTimer = useRef(null);

  const hasFilters = search || filterType || filterPriority || filterRead || dateFrom || dateTo;

  const buildParams = useCallback((p) => {
    const params = new URLSearchParams({ page: p, per_page: itemsPerPage });
    if (search) params.set('search', search);
    if (filterType) params.set('type', filterType);
    if (filterPriority) params.set('priority', filterPriority);
    if (filterRead) params.set('read', filterRead);
    if (dateFrom) params.set('date_from', dateFrom);
    if (dateTo) params.set('date_to', dateTo);
    return params.toString();
  }, [search, filterType, filterPriority, filterRead, dateFrom, dateTo, itemsPerPage]);

  const fetchInbox = useCallback(async (p = 1) => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.get(`/notifications/inbox?${buildParams(p)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications || []);
      setTotal(res.data.total || 0);
      setPage(p);
    } catch (err) {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [token, buildParams]);

  // Refetch when filters/page-size change (except search which is debounced)
  useEffect(() => {
    fetchInbox(1);
  }, [filterType, filterPriority, filterRead, dateFrom, dateTo, itemsPerPage]); // eslint-disable-line

  // Debounce search
  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => fetchInbox(1), 350);
    return () => clearTimeout(searchTimer.current);
  }, [search]); // eslint-disable-line

  const clearFilters = () => {
    setSearch('');
    setFilterType('');
    setFilterPriority('');
    setFilterRead('');
    setDateFrom('');
    setDateTo('');
  };

  const markAsRead = async (id) => {
    try {
      await api.post(`/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => n.id === id ? { ...n, is_read: true } : n));
    } catch (err) { /* ignore */ }
  };

  const markAllRead = async () => {
    setMarkingAll(true);
    try {
      const unread = notifications.filter((n) => !n.is_read);
      await Promise.all(
        unread.map((n) =>
          api.post(`/notifications/${n.id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } })
        )
      );
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) { /* ignore */ } finally {
      setMarkingAll(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / itemsPerPage));
  const unreadCount = notifications.filter((n) => !n.is_read).length;

  // Page button renderer (matches CompletedProjects style)
  const renderPageButtons = () => {
    const pages = [];
    const delta = 2;
    const left = Math.max(1, page - delta);
    const right = Math.min(totalPages, page + delta);
    if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
    return pages.map((p, idx) =>
      p === '...' ? (
        <span key={`e-${idx}`} className="px-2 py-1 text-gray-500 text-sm">…</span>
      ) : (
        <button
          key={p}
          onClick={() => fetchInbox(p)}
          className={`px-3 py-1 rounded text-sm font-medium border transition-colors ${
            p === page ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
          }`}
        >
          {p}
        </button>
      )
    );
  };

  return (
    <div className="p-6">
      {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Bell className="w-7 h-7 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Notifications</h1>
            {isAdmin && <p className="text-xs text-gray-500">Viewing all notifications (admin)</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              onClick={markAllRead}
              disabled={markingAll}
              className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <CheckCircle className="w-4 h-4" />
              {markingAll ? 'Marking…' : 'Mark all read'}
            </button>
          )}
          <button
            onClick={() => fetchInbox(page)}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          {canSend && (
            <button
              onClick={() => setSendModalOpen(true)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
            >
              <Send className="w-4 h-4" />
              Send Notification
            </button>
          )}
        </div>
      </div>

      {/* Filter Bar */}
      <div className={`mb-4 p-3 rounded-lg border flex flex-wrap items-center gap-3 ${hasFilters ? 'bg-sky-50 border-sky-300' : 'bg-white border-gray-200'}`}>
        {hasFilters && (
          <button
            onClick={clearFilters}
            className="px-3 py-2 text-sm text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
          >
            Clear
          </button>
        )}
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search title, message, sender…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[230px]"
          />
        </div>
        {/* Type */}
        <select
          value={filterType}
          onChange={(e) => { setFilterType(e.target.value); }}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Types</option>
          <option value="manual">Manual</option>
          <option value="approval_rejected">Rejection Alert</option>
          <option value="approval_approved">Approval Alert</option>
          <option value="no_jobs">No Jobs</option>
          <option value="task_hold">On Hold</option>
          <option value="task_pause">Paused</option>
          <option value="task_overtime">Overtime</option>
        </select>
        {/* Priority */}
        <select
          value={filterPriority}
          onChange={(e) => setFilterPriority(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Priorities</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </select>
        {/* Read status */}
        <select
          value={filterRead}
          onChange={(e) => setFilterRead(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All (Read & Unread)</option>
          <option value="unread">Unread only</option>
          <option value="read">Read only</option>
        </select>
        {/* Date range */}
        <label className="text-sm text-gray-600 whitespace-nowrap">From:</label>
        <input
          type="date"
          value={dateFrom}
          onChange={(e) => setDateFrom(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <label className="text-sm text-gray-600 whitespace-nowrap">To:</label>
        <input
          type="date"
          value={dateTo}
          onChange={(e) => setDateTo(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>

      {/* List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && notifications.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <RefreshCw className="w-6 h-6 animate-spin mr-2" />
            Loading…
          </div>
        ) : notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Bell className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-lg font-medium">No notifications found</p>
            <p className="text-sm mt-1">{hasFilters ? 'Try adjusting your filters.' : "You'll see alerts and messages here."}</p>
          </div>
        ) : (
          <ul className="divide-y divide-gray-100">
            {notifications.map((n) => (
              <li
                key={n.id}
                className={`flex gap-4 px-5 py-4 transition-colors ${n.is_read ? 'bg-white' : 'bg-blue-50'}`}
              >
                {/* Status dot */}
                <div className="mt-1.5 flex-shrink-0">
                  <div className={`w-2.5 h-2.5 rounded-full ${
                    n.is_read ? 'bg-gray-300' : n.priority === 'high' ? 'bg-red-500' : 'bg-blue-500'
                  }`} />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2 flex-wrap">
                      <TypeBadge type={n.type} />
                      {n.priority === 'high' && (
                        <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">
                          High Priority
                        </span>
                      )}
                      <span className="font-semibold text-gray-900">{n.title}</span>
                    </div>
                    {!n.is_read && (
                      <button
                        onClick={() => markAsRead(n.id)}
                        className="text-xs text-blue-600 hover:underline flex-shrink-0"
                      >
                        Mark read
                      </button>
                    )}
                  </div>

                  <p className="mt-1 text-sm text-gray-600 whitespace-pre-line">{n.message}</p>

                  {(n.project_name || n.task_name || n.job_code) && (
                    <div className="mt-2 flex flex-wrap gap-3 text-xs text-gray-500">
                      {n.job_code && (
                        <span className="px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-mono">
                          Job: {n.job_code}
                        </span>
                      )}
                      {n.project_name && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded">Project: {n.project_name}</span>
                      )}
                      {n.task_name && (
                        <span className="px-2 py-0.5 bg-gray-100 rounded">Task: {n.task_name}</span>
                      )}
                    </div>
                  )}

                  {/* Admin: show who it was sent to */}
                  {isAdmin && Array.isArray(n.recipient_ids) && (
                    <div className="mt-1 text-xs text-gray-400">
                      {n.recipient_ids.length === 0
                        ? 'Sent to: All users'
                        : (() => {
                            const names = Array.isArray(n.recipient_names) && n.recipient_names.length
                              ? n.recipient_names
                              : n.recipient_ids.map((_, i) => `User ${i + 1}`);
                            if (names.length <= 5) {
                              return `Sent to: ${names.join(', ')}`;
                            }
                            return `Sent to: ${names.slice(0, 3).join(', ')} +${names.length - 3} more`;
                          })()
                      }
                    </div>
                  )}

                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                    {n.sender_name && <span>From: <span className="text-gray-600">{n.sender_name}</span></span>}
                    {n.created_at && (
                      <span>
                        {new Date(n.created_at.endsWith('Z') ? n.created_at : n.created_at + 'Z').toLocaleString('en-GB', {
                          day: '2-digit', month: 'short', year: 'numeric',
                          hour: '2-digit', minute: '2-digit'
                        })}
                      </span>
                    )}
                    {n.is_read && (
                      <span className="flex items-center gap-1 text-green-600">
                        <CheckCircle className="w-3 h-3" /> Read
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Pagination footer */}
      <div className="mt-4 px-4 py-3 bg-white border border-gray-200 rounded-xl flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">Items per page:</span>
          <select
            value={itemsPerPage}
            onChange={(e) => { setItemsPerPage(Number(e.target.value)); }}
            className="px-3 py-1.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {[25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          <span className="text-sm text-gray-500">
            {total > 0
              ? `${(page - 1) * itemsPerPage + 1}–${Math.min(page * itemsPerPage, total)} of ${total}`
              : '0 results'}
          </span>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => fetchInbox(1)}
            disabled={page === 1 || loading}
            className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >«</button>
          <button
            onClick={() => fetchInbox(page - 1)}
            disabled={page === 1 || loading}
            className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >‹</button>
          {renderPageButtons()}
          <button
            onClick={() => fetchInbox(page + 1)}
            disabled={page === totalPages || loading}
            className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >›</button>
          <button
            onClick={() => fetchInbox(totalPages)}
            disabled={page === totalPages || loading}
            className="px-2 py-1 rounded text-sm border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40"
          >»</button>
        </div>
      </div>

      {/* Send Modal */}
      {sendModalOpen && (
        <SendNotificationModal
          token={token}
          onClose={() => setSendModalOpen(false)}
          onSent={() => fetchInbox(1)}
        />
      )}
    </div>
  );
};

export default Notifications;
